import { Content } from '@radix-ui/react-dialog'

import { cn, VisuallyHidden } from '@seen/ui'
import { IconX } from '@tabler/icons-react'
import { cva, type VariantProps } from 'class-variance-authority'
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
} from 'react'
import { ScrollArea } from '#src/components/core/scroll-area/ScrollArea.tsx'
import { SheetClose } from '#src/components/core/sheet/SheetClose.tsx'
import { SheetOverlay } from '#src/components/core/sheet/SheetOverlay.tsx'
import { SheetPortal } from '#src/components/core/sheet/SheetPortal.tsx'
import { SheetTitle } from '#src/components/core/sheet/SheetTitle.tsx'

type ContentProps = ComponentPropsWithoutRef<typeof Content>

export interface SheetContentProps extends ContentProps {
  asChild?: ContentProps['asChild']
  forceMount?: ContentProps['forceMount']
  onOpenAutoFocus?: ContentProps['onOpenAutoFocus']
  onCloseAutoFocus?: ContentProps['onCloseAutoFocus']
  onEscapeKeyDown?: ContentProps['onEscapeKeyDown']
  onPointerDownOutside?: ContentProps['onPointerDownOutside']
  onInteractOutside?: ContentProps['onInteractOutside']
  side?: VariantProps<typeof variants>['side']
  hideClose?: boolean
  // Skips the built-in ScrollArea around children, for content that owns its
  // scrolling (e.g. the facesheet sheet's per-tab scroll areas).
  disableScrollArea?: boolean
  /**
   * Data attributes:
   *   [data-state]: "open" | "closed"
   */
  className?: ContentProps['className']
}

const variants = cva(
  'fixed gap-4 bg-background shadow-lg transition ease-in-out data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:duration-200 data-[state=open]:duration-200',
  {
    variants: {
      side: {
        top: 'data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 border-b',
        bottom:
          'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 border-t',
        left: 'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full border-r',
        right:
          'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l',
      },
    },
    defaultVariants: {
      side: 'right',
    },
  }
)

export const SheetContent = forwardRef<
  ElementRef<typeof Content>,
  SheetContentProps
>(
  (
    {
      side = 'right',
      className,
      children,
      hideClose,
      disableScrollArea,
      ...delegated
    },
    ref
  ) => (
    <SheetPortal>
      <SheetOverlay />
      <Content
        ref={ref}
        data-slot="sheet-content"
        className={cn(variants({ side }), className)}
        aria-describedby={undefined}
        {...delegated}
        onPointerDownOutside={(e) => {
          // https://github.com/radix-ui/primitives/issues/2690#issuecomment-1945449832
          if (
            e.target instanceof Element &&
            e.target.closest('[data-sonner-toast]')
          ) {
            e.preventDefault()
          }
          delegated.onPointerDownOutside?.(e)
        }}
      >
        <VisuallyHidden>
          <SheetTitle>Sheet</SheetTitle>
        </VisuallyHidden>
        {disableScrollArea ? (
          children
        ) : (
          <ScrollArea scrollRestorationId={null}>{children}</ScrollArea>
        )}
        <SheetClose
          className={cn(
            'absolute top-4 right-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary',
            hideClose && 'sr-only'
          )}
        >
          <IconX className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </SheetClose>
      </Content>
    </SheetPortal>
  )
)
SheetContent.displayName = Content.displayName
